// Copied from https://github.com/ProseMirror/prosemirror-markdown
//
// Copyright (C) 2015-2017 by Marijn Haverbeke <marijnh@gmail.com> and others
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.
import type { Node as ProseMirrorNode, DOMOutputSpec } from "prosemirror-model";
import { Schema } from "prosemirror-model";
import { tableNodes as originalTableNodes } from "prosemirror-tables";

import allowedElementsArray from "../../generated/allowedElements.json";

import { switchableListNodes } from "./features/switchableList/schema";
import { linkCardClass } from "./plugins/link/constants";
import { convertOneLine } from "./utils/convertOneLine";
import { removeProtocolAndTrailingSlash } from "./utils/removeProtocolAndTrailingSlash";

import { isCopiedFromProseMirror } from "~/client/components/richTextEditor/utils/isCopiedFromProseMirror";
import { emojiNode } from "~/client/features/emoji/richTextEditor/schema";

// The same list with turndown one.
// https://github.com/domchristie/turndown/blob/master/src/utilities.js
const blockElementNames = [
  "address",
  "article",
  "aside",
  "audio",
  "blockquote",
  "body",
  "canvas",
  "center",
  "dd",
  "dir",
  "div",
  "dl",
  "dt",
  "fieldset",
  "figcaption",
  "figure",
  "footer",
  "form",
  "frameset",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "header",
  "hgroup",
  "hr",
  "html",
  "isindex",
  "li",
  "main",
  "menu",
  "nav",
  "noframes",
  "noscript",
  "ol",
  "output",
  "p",
  "pre",
  "section",
  "table",
  "tbody",
  "td",
  "tfoot",
  "th",
  "thead",
  "tr",
  "ul",
];

const markElementNames = [
  "font",
  "b",
  "em",
  "i",
  "strong",
  "u",
  "a",
  "s",
  "small",
  "sub",
  "sup",
  "abbr",
  "cite",
  "code",
  "dfn",
  "mark",
  "q",
  "samp",
  "strike",
  "time",
  "var",
  "bdi",
  "bdo",
  "data",
  "del",
  "ins",
  "span",
];

// Tag names that are allowed by Kibela sanitizer,
// but the Rich Text Editor does not support writing it (only displaying support).
const { allowedElements } = allowedElementsArray;
const allowedBlockElements = allowedElements.filter((e) => blockElementNames.includes(e));
const allowedInlineElements = allowedElements.filter(
  (e) => !blockElementNames.includes(e) && !markElementNames.includes(e),
);
const allowedMarkElements = allowedElements.filter((e) => markElementNames.includes(e));

// It should be less than the default priority, which is 50.
const AllowedElementsPriority = 10;

const LOOSE_LIST_ITEM_CLASS = "is-loose-list-item";

const convertAttrsToObject = (attrs: NamedNodeMap) => {
  const resp: any = {};
  for (const attr of attrs) {
    resp[attr.name] = attr.value;
  }
  return resp;
};

const isRootNodeCopiedFromProseMirror = (dom: HTMLElement) => {
  const rootNode = dom.getRootNode();

  if (typeof HTMLElement !== "undefined" && rootNode instanceof HTMLElement) {
    return isCopiedFromProseMirror(rootNode);
  } else {
    return false; // e.g. Document will return on first load
  }
};

const isTightList = (dom: HTMLElement) => {
  let tight = true;
  dom.querySelectorAll("li > p").forEach((elm) => {
    if (tight && elm.parentElement!.parentElement === dom) {
      if (isRootNodeCopiedFromProseMirror(dom)) {
        tight = !dom.classList.contains(LOOSE_LIST_ITEM_CLASS);
      } else {
        tight = false;
      }
    }
  });
  return tight;
};

const baseSchemaSpec = {
  nodes: {
    doc: {
      content: "(block | tableWrapper | linkCard)+",
    },

    paragraph: {
      content: "inline*",
      group: "block",
      parseDOM: [{ tag: "p" }],
      toDOM() {
        return ["p", 0];
      },
    },

    blockquote: {
      attrs: { className: { default: "" } },
      content: "block+",
      group: "block",
      defining: true,
      parseDOM: [
        {
          tag: "blockquote",
          getAttrs(dom: HTMLElement) {
            const className = dom.className;
            return { className };
          },
        },
      ],
      toDOM() {
        return ["blockquote", 0];
      },
    },

    horizontalRule: {
      group: "block",
      parseDOM: [{ tag: "hr" }],
      toDOM() {
        return ["div", ["hr"]];
      },
    },

    heading: {
      attrs: { level: { default: 1 } },
      content: "inline*",
      group: "block",
      defining: true,
      parseDOM: [
        { tag: "h1", attrs: { level: 1 } },
        { tag: "h2", attrs: { level: 2 } },
        { tag: "h3", attrs: { level: 3 } },
        { tag: "h4", attrs: { level: 4 } },
        { tag: "h5", attrs: { level: 5 } },
        { tag: "h6", attrs: { level: 6 } },
      ],
      toDOM(node) {
        return ["h" + node.attrs.level, 0];
      },
    },

    blockLatex: {
      attrs: {
        html: { default: null },
        domParser: { default: null },
        source: { default: null },
      },
      group: "block",
      draggable: true,
      parseDOM: [
        {
          tag: "div.latex-container-display",
          getAttrs(dom: HTMLElement) {
            const domParser = typeof DOMParser !== "undefined" ? new DOMParser() : null;
            const source = dom.querySelector('script[type="math/tex;mode=display"]')!.textContent;
            return { html: dom.outerHTML, domParser, source };
          },
        },
      ],
      toDOM(node: ProseMirrorNode) {
        const parser = node.attrs.domParser;
        return parser.parseFromString(node.attrs.html, "text/html").querySelector("div.latex-container-display");
      },
    },

    inlineLatex: {
      attrs: {
        html: { default: null },
        domParser: { default: null },
        source: { default: null },
      },
      inline: true,
      group: "inline",
      draggable: true,
      parseDOM: [
        {
          tag: "span.latex-container-inline",
          getAttrs(dom: HTMLElement) {
            const domParser = typeof DOMParser !== "undefined" ? new DOMParser() : null;
            const source = dom.querySelector('script[type="math/tex;mode=inline"]')!.textContent;
            return { html: dom.outerHTML, domParser, source };
          },
        },
      ],
      toDOM(node: ProseMirrorNode) {
        const parser = node.attrs.domParser;
        return parser.parseFromString(node.attrs.html, "text/html").querySelector("span.latex-container-inline");
      },
    },

    scriptSandbox: {
      attrs: {
        html: { default: null },
        domParser: { default: null },
        source: { default: null },
      },
      group: "block",
      draggable: true,
      parseDOM: [
        {
          tag: "div.iframeWrapper",
          getAttrs(dom: HTMLElement) {
            const domParser = typeof DOMParser !== "undefined" ? new DOMParser() : null;
            const source = dom.dataset.scriptSource;
            return { html: dom.outerHTML, domParser, source };
          },
        },
      ],
      toDOM(node: ProseMirrorNode) {
        const parser = node.attrs.domParser;
        return parser.parseFromString(node.attrs.html, "text/html").querySelector("div.iframeWrapper");
      },
    },

    plantUml: {
      attrs: { source: { default: null }, imgAlt: { default: "" }, imgSrc: { default: null } },
      group: "block",
      draggable: true,
      parseDOM: [
        {
          tag: "div.plantuml",
          getAttrs(dom: HTMLElement) {
            const source = dom.dataset.plantumlSource;
            const img = dom.querySelector("img")!;
            return { source, imgAlt: img.alt, imgSrc: img.src };
          },
        },
      ],
      toDOM(node: ProseMirrorNode) {
        return ["div", { class: "plantuml" }, ["img", { alt: node.attrs.imgAlt, src: node.attrs.imgSrc }]];
      },
    },

    // CodeBlock
    codeFrame: {
      content: "codeLabel? codeHighlight",
      group: "block",
      marks: "",
      parseDOM: [{ tag: "div.code-frame" }],
      toDOM(_node) {
        return ["div", { class: "code-frame" }, 0];
      },
    },

    codeLabel: {
      content: "text*",
      code: true,
      marks: "",
      defining: true,
      parseDOM: [{ tag: "div.code-label" }],
      toDOM(_node) {
        return ["div", { class: "code-label" }, 0];
      },
    },

    codeHighlight: {
      content: "codeContent",
      marks: "",
      defining: true,
      parseDOM: [
        {
          tag: "pre.highlight",
          preserveWhitespace: "full",
          contentElement(dom: Node) {
            // If we have code blocks in the content, we have to remove unneeded hard break on the end of code.
            const hardBreakOnTheEndOfCodeRegex = /\n$/;
            const content = dom.lastChild;
            if (content) {
              content.textContent = content.textContent!.replace(hardBreakOnTheEndOfCodeRegex, "");
            } else {
              throw "unexpected state!";
            }
            return dom;
          },
        },
      ],
      toDOM(_node) {
        return ["pre", { class: "highlight codeHighlight" }, 0];
      },
    },

    codeContent: {
      content: "text*",
      attrs: { dataLang: { default: null } },
      code: true,
      marks: "",
      parseDOM: [
        {
          tag: "code[data-lang]",
          getAttrs(dom: HTMLElement) {
            return { dataLang: dom.getAttribute("data-lang") };
          },
        },
      ],
      toDOM(node) {
        return ["code", { "data-lang": node.attrs["data-lang"], class: "codeContent" }, 0];
      },
    },

    // List
    orderedList: {
      content: "listItem+",
      group: "block",
      attrs: { order: { default: 1 }, tight: { default: true } },
      parseDOM: [
        {
          tag: "ol",
          getAttrs(dom: HTMLElement) {
            return {
              order: dom.hasAttribute("start") ? +dom.getAttribute("start")! : 1,
              tight: isTightList(dom),
            };
          },
        },
      ],
      toDOM(node) {
        return [
          "ol",
          {
            start: node.attrs.order === 1 ? null : node.attrs.order,
            class: node.attrs.tight ? null : LOOSE_LIST_ITEM_CLASS,
          },
          0,
        ];
      },
    },

    bulletList: {
      content: "listItem+",
      group: "block",
      attrs: { tight: { default: true } },
      parseDOM: [
        {
          tag: "ul",
          getAttrs: (dom: HTMLElement) => ({ tight: isTightList(dom) }),
        },
      ],
      toDOM(node) {
        return ["ul", { class: node.attrs.tight ? null : LOOSE_LIST_ITEM_CLASS }, 0];
      },
    },

    listItem: {
      content: "(checkBoxInListItem? block+) | looseListParagraph",
      defining: true,
      parseDOM: [{ tag: "li" }],
      // ListItemView applies task-list-item class.
      toDOM() {
        return ["li", 0];
      },
    },

    looseListParagraph: {
      content: "checkBoxInListItem? block+",
      parseDOM: [{ tag: "p", skip: true, priority: 60 }],
      toDOM() {
        return ["p", 0];
      },
    },

    checkBoxInListItem: {
      selectable: false,
      attrs: { checked: { default: false } },
      parseDOM: [
        {
          tag: "span.task-list-item-checkbox-wrapper",
          getAttrs(dom: HTMLElement) {
            const input = dom.querySelector("input")!;
            return { checked: input.checked };
          },
        },
      ],
      toDOM(node: ProseMirrorNode) {
        return [
          // Workaround: Firefox cannot fire event input inner contenteditable element
          "span",
          { class: "task-list-item-checkbox-wrapper" },
          [
            "input",
            {
              type: "checkbox",
              class: "task-list-item-checkbox",
              // ProseMirror is aware of false as "false".
              // So it needs converting false to null.
              checked: node.attrs.checked || null,
            },
          ],
        ];
      },
    },

    text: {
      group: "inline",
    },

    mention: {
      inline: true,
      attrs: {
        mention: { default: null },
        href: { default: null },
        avatar: { default: null },
      },
      group: "inline",
      parseDOM: [
        {
          tag: "a[data-mention]",
          priority: 60, // Higher than the default (50)
          getAttrs(dom: HTMLElement) {
            const img = dom.querySelector("img")!;
            return {
              mention: dom.getAttribute("data-mention"),
              href: dom.getAttribute("href"),
              avatar: {
                srcset: img.getAttribute("srcset"),
                src: img.getAttribute("src"),
              },
            };
          },
        },
      ],
      toDOM(node) {
        return [
          "a",
          { class: "user-accountWithAvatar user-mention", "data-mention": node.attrs.mention, href: node.attrs.href },
          ["img", { class: "avatar is-avatar-small", ...node.attrs.avatar }],
          ["span", {}, node.attrs.mention + " "],
        ];
      },
    },

    groupLink: {
      inline: true,
      attrs: {
        href: { default: null },
        name: { default: null },
      },
      group: "inline",
      parseDOM: [
        {
          tag: "a.entry-titleWithIcon.is-group",
          priority: 60,
          getAttrs(dom: HTMLElement) {
            return {
              href: dom.getAttribute("href"),
              name: dom.innerText,
            };
          },
        },
      ],
      toDOM(node) {
        return [
          "a",
          { class: "entry-titleWithIcon is-titleWithIcon-inline is-group", href: node.attrs.href },
          ["span", node.attrs.name],
        ];
      },
    },

    folderLink: {
      inline: true,
      attrs: {
        href: { default: null },
        name: { default: null },
      },
      group: "inline",
      parseDOM: [
        {
          tag: "a.entry-titleWithIcon.is-folder",
          priority: 60,
          getAttrs(dom: HTMLElement) {
            return {
              href: dom.getAttribute("href"),
              name: dom.innerText,
            };
          },
        },
      ],
      toDOM(node) {
        return [
          "a",
          { class: "entry-titleWithIcon is-titleWithIcon-inline is-folder", href: node.attrs.href },
          [
            "http://www.w3.org/2000/svg svg",
            { class: "ks ks-folder", focusable: "false" },
            // eslint-disable-next-line @typescript-eslint/naming-convention
            ["use", { "http://www.w3.org/1999/xlink xlink:href": "#ks-folder" }],
          ],
          ["span", { class: "entry-titleWithIcon-title" }, node.attrs.name],
        ];
      },
    },

    noteLink: {
      inline: true,
      attrs: {
        href: { default: null },
        title: { default: null },
        avatar: { default: null },
      },
      group: "inline",
      parseDOM: [
        {
          tag: "a.entry-titleWithIcon.is-note",
          priority: 60,
          getAttrs(dom: HTMLElement) {
            const img = dom.querySelector("img");
            const titleDom = dom.querySelector(".entry-titleWithIcon-title") as HTMLSpanElement;
            if (!titleDom) {
              return false;
            }

            if (img) {
              // coediting: false
              return {
                href: dom.getAttribute("href"),
                title: titleDom.innerText,
                avatar: {
                  srcset: img.getAttribute("srcset"),
                  src: img.getAttribute("src"),
                },
              };
            } else {
              // coediting: true
              return {
                href: dom.getAttribute("href"),
                title: titleDom.innerText,
              };
            }
          },
        },
      ],
      toDOM(node) {
        if (node.attrs.avatar) {
          return [
            "a",
            { class: "entry-titleWithIcon is-titleWithIcon-inline", href: node.attrs.href },
            ["img", { class: "avatar is-avatar-small", ...node.attrs.avatar }],
            ["span", { class: "entry-titleWithIcon-title" }, node.attrs.title],
          ];
        } else {
          return [
            "a",
            { class: "entry-titleWithIcon is-titleWithIcon-inline", href: node.attrs.href },
            [
              "http://www.w3.org/2000/svg svg",
              { class: "ks ks-wiki", focusable: "false" },
              ["use", { "http://www.w3.org/1999/xlink xlink:href": "#ks-wiki" }],
            ],
            ["span", { class: "entry-titleWithIcon-title" }, node.attrs.title],
          ];
        }
      },
    },

    // For attachment image
    kibelaImage: {
      inline: true,
      attrs: {
        htmlAttrs: { default: {} },
      },
      group: "inline",
      draggable: true,
      parseDOM: [
        {
          tag: "img[data-src]",
          getAttrs(dom: HTMLElement) {
            const htmlAttrs = convertAttrsToObject(dom.attributes);
            htmlAttrs["src"] = htmlAttrs["data-src-original"];
            // Remove attributes that are injected by KibelaMarkdown.
            // see KibelaMarkdown::MatoFilters::InternalLink#fixup_attachment_tag
            delete htmlAttrs["data-src"];
            delete htmlAttrs["data-src-original"];
            delete htmlAttrs["data-lazy"];
            return { htmlAttrs };
          },
        },
      ],
      toDOM(node) {
        return ["img", { ...node.attrs.htmlAttrs }];
      },
    },

    // attachment media except image
    mediaAttachment: {
      inline: true,
      attrs: {
        href: { default: null },
        type: { default: null },
        // eslint-disable-next-line @typescript-eslint/naming-convention
        "data-meta": { default: null },
      },
      content: "text*",
      group: "inline",
      parseDOM: [
        {
          tag: "a.attachment",
          priority: 60,
          getAttrs(dom: HTMLElement) {
            return {
              // XXX: Do NOT use dom.href because dom.href unexpectedly modifies
              //      relative URL (like /attachments/xxx) to absolute URL (like http://lvh.me:300/attachments/xxx).
              href: dom.getAttribute("href"),
              type: dom.getAttribute("type"),
              "data-meta": dom.getAttribute("data-meta"),
            };
          },
        },
      ],
      toDOM(node) {
        return ["a", { ...node.attrs, class: "attachment" }, 0];
      },
    },

    officeAttachment: {
      inline: true,
      group: "inline",
      attrs: {
        href: { default: null },
        key: { default: null },
        text: { default: null },
      },
      parseDOM: [
        {
          tag: "a.attachment.has-file-preview",
          priority: 70,
          getAttrs(dom: HTMLElement) {
            return {
              href: dom.getAttribute("href"),
              key: dom.getAttribute("data-key"),
              text: dom.textContent,
            };
          },
        },
      ],
      toDOM(node) {
        return [
          "a",
          // eslint-disable-next-line @typescript-eslint/naming-convention
          { href: node.attrs.href, target: "_blank", class: "attachment has-file-preview", "data-key": node.attrs.key },
          [
            "http://www.w3.org/2000/svg svg",
            { class: "ks ks-preview", focusable: "false" },
            // eslint-disable-next-line @typescript-eslint/naming-convention
            ["use", { "http://www.w3.org/1999/xlink xlink:href": "#ks-preview" }],
          ],
          ["span", node.attrs.text],
        ];
      },
    },

    hard_break: {
      inline: true,
      group: "inline",
      selectable: false,
      parseDOM: [{ tag: "br" }],
      toDOM() {
        return ["br"];
      },
    },

    footnoteRef: {
      inline: true,
      attrs: { fnId: { default: null }, text: { default: null } },
      group: "inline",
      parseDOM: [
        {
          tag: "sup.footnote-ref",
          getAttrs(dom: HTMLElement) {
            const aTag = dom.querySelector("a");
            if (!aTag) {
              return false;
            }
            return { fnId: aTag.hash.replace("#fn-", ""), text: aTag.textContent };
          },
        },
      ],
      toDOM(node) {
        return [
          "sup",
          {
            class: "footnote-ref",
          },
          node.attrs.text,
        ];
      },
    },

    footnotes: {
      content: "footnoteItem*",
      group: "block",
      parseDOM: [
        {
          tag: "section.footnotes",
        },
      ],
      toDOM(_node: ProseMirrorNode) {
        return ["section", { class: "footnotes" }, ["ol", { class: "footnote-list" }, 0]];
      },
    },

    footnoteItem: {
      group: "block",
      content: "paragraph*",
      attrs: { fnId: { default: null } },
      parseDOM: [
        {
          tag: "li",
          context: "footnotes//",
          priority: 60,
          getAttrs(dom: HTMLElement) {
            const anchor = dom.querySelector<HTMLAnchorElement>("a");
            if (!anchor) {
              return;
            }
            const href = anchor.getAttribute("href");
            const fnId = href!.replace("#fnref-", "");
            return { fnId };
          },
        },
      ],
      toDOM(node: ProseMirrorNode) {
        return ["li", { class: "footnote-item", id: `#fn-${node.attrs.fnId}` }, 0];
      },
    },

    footnoteAnchor: {
      inline: true,
      group: "inline",
      context: "footItem//",
      marks: "link",
      attrs: {
        fnId: { default: null },
      },
      parseDOM: [
        {
          tag: "a.footnote-backref",
          priority: 60,
          getAttrs(dom: HTMLAnchorElement) {
            const fnId = dom.getAttribute("href")!.replace("#fnref-", "");
            return { fnId };
          },
        },
      ],
      toDOM(node: ProseMirrorNode) {
        return ["a", { class: "footnote-backref", href: `#fnref-${node.attrs.fnId}` }, "↩"];
      },
    },

    allowedBlockElement: {
      attrs: { tag: { default: null }, htmlAttrs: { default: {} } },
      group: "block",
      content: "block*",
      parseDOM: allowedBlockElements.map((tag) => ({
        tag,
        priority: AllowedElementsPriority,
        getAttrs(dom: HTMLElement) {
          return { tag, htmlAttrs: convertAttrsToObject(dom.attributes) };
        },
      })),
      toDOM(node) {
        return [node.attrs.tag, node.attrs.htmlAttrs, 0];
      },
    },

    allowedInlineElement: {
      attrs: { tag: { default: null }, htmlAttrs: { default: {} } },
      inline: true,
      group: "inline",
      content: "inline*",
      parseDOM: allowedInlineElements.map((tag) => ({
        tag,
        priority: AllowedElementsPriority,
        getAttrs(dom: HTMLElement) {
          return { tag, htmlAttrs: convertAttrsToObject(dom.attributes) };
        },
      })),
      toDOM(node) {
        return [node.attrs.tag, node.attrs.htmlAttrs, 0];
      },
    },

    tableWrapper: {
      tag: "div.tableWrapper",
      content: "table",
      parseDOM: [
        {
          tag: "div.tableWrapper",
        },
      ],
      toDOM() {
        return ["div", { class: "tableWrapper rich-text-editor-table" }, 0];
      },
    },

    linkCard: {
      group: "block",
      attrs: {
        url: { default: null },
        title: { default: null },
        description: { default: null },
        faviconUrl: { default: null },
        imgUrl: { default: null },
      },
      draggable: true,
      parseDOM: [
        {
          tag: `a.${linkCardClass}`,
          priority: 60,
          getAttrs(anchor: HTMLAnchorElement) {
            const url = anchor.href;
            const title = anchor.querySelector(`div > div:first-child`)?.textContent;

            const maybeDescriptionDiv = anchor.querySelector(`div > div:nth-child(2)`);
            const urlDiv = anchor.querySelector(`div > div:last-child`);
            const description =
              maybeDescriptionDiv && maybeDescriptionDiv !== urlDiv ? maybeDescriptionDiv.textContent : null;

            const faviconUrl = urlDiv!.querySelector<HTMLImageElement>("img")?.src;

            const img = anchor.lastElementChild as HTMLImageElement;
            const imgUrl = img ? img.src : null;

            return { url, title, description, imgUrl, faviconUrl };
          },
        },
      ],
      toDOM(node: ProseMirrorNode) {
        const mainContent: Array<DOMOutputSpec> = [];

        const titleDiv: DOMOutputSpec = ["div", convertOneLine(node.attrs.title)];
        mainContent.push(titleDiv);

        if (node.attrs.description) {
          const descriptionDiv: DOMOutputSpec = ["div", convertOneLine(node.attrs.description)];
          mainContent.push(descriptionDiv);
        }

        const shortedUrlSpan: DOMOutputSpec = ["span", {}, removeProtocolAndTrailingSlash(node.attrs.url)];
        const urlContent: DOMOutputSpec = node.attrs.faviconUrl
          ? ["span", ["img", { src: node.attrs.faviconUrl, alt: "" }], shortedUrlSpan]
          : ["span", shortedUrlSpan];
        const urlDiv: DOMOutputSpec = ["div", urlContent];
        mainContent.push(urlDiv);

        const main: DOMOutputSpec = ["div", ...mainContent];
        const anchorContent: Array<DOMOutputSpec> = [main];
        if (node.attrs.imgUrl) {
          anchorContent.push(["img", { src: node.attrs.imgUrl }]);
        }

        return ["a", { href: node.attrs.url, class: linkCardClass }, ...anchorContent];
      },
    },
  },

  marks: {
    em: {
      parseDOM: [{ tag: "i" }, { tag: "em" }],
      toDOM() {
        return ["em"];
      },
    },

    strong: {
      parseDOM: [{ tag: "b" }, { tag: "strong" }],
      toDOM() {
        return ["strong"];
      },
    },

    strikethrough: {
      parseDOM: [{ tag: "del" }],
      toDOM() {
        return ["del"];
      },
    },

    link: {
      attrs: {
        href: { default: null },
        title: { default: null },
      },
      inclusive: false,
      parseDOM: [
        {
          tag: "a[href]",
          getAttrs(dom: HTMLElement) {
            return { href: dom.getAttribute("href"), title: dom.getAttribute("title") };
          },
        },
      ],
      toDOM(mark) {
        return ["a", mark.attrs];
      },
    },

    code: {
      parseDOM: [{ tag: "code" }],
      toDOM() {
        return ["code", 0];
      },
    },

    allowedMarks: {
      attrs: { tag: { default: null }, htmlAttrs: { default: {} } },
      parseDOM: allowedMarkElements.map((tag) => ({
        tag,
        priority: AllowedElementsPriority,
        getAttrs(dom: HTMLElement) {
          return { tag, htmlAttrs: convertAttrsToObject(dom.attributes) };
        },
      })),
      toDOM(node) {
        return [node.attrs.tag, node.attrs.htmlAttrs, 0];
      },
    },
  },
};

const tableNodes = originalTableNodes({
  cellContent: "inline*",
  cellAttributes: { shouldRenderToBrElement: { default: true } },
});

// 制約を1つ以上としなければmarkdownへの変換ができず、スタイルも崩れてしまう
// ref: https://github.com/bitjourney/kibela/pull/14460
tableNodes.table_row.content = "(table_cell | table_header)+";

export const schemaSpec = {
  nodes: {
    ...baseSchemaSpec.nodes,
    ...tableNodes,
    ...switchableListNodes,
    emoji: emojiNode,
  },
  marks: baseSchemaSpec.marks,
};

// ::Schema Document schema for the data model used by CommonMark.
export const schema = new Schema<keyof typeof schemaSpec.nodes, keyof typeof schemaSpec.marks>(schemaSpec as any);
export type SchemaType = typeof schema;
